import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import {States} from "../../shared/config/constant.module";
import template from './deletedGobWeightReport.tpl.html';
import {Plant} from "../../models/navigation";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {StateParams} from "../../common";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import ReportServiceName, {ReportService} from "../../services/reports/report.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";

let requires = [LoaderServiceName, ReportServiceName, NavigationServiceName];

interface DeletedGobWeightReportController {
  plants: Plant[];
  selectedPlant: Plant;
  startDate: Date;
  endDate: Date;
  form: angular.IFormController;

  results: apsViewModels.DeletedGobWeightReportItem[];
  searchPerformed: boolean;

  generateReport(): void;
  formatTime(time: Date): string;
}

export interface DeletedGobWeightReportParams extends StateParams {
  reportPlantId: string;
  startDate: string;
  endDate: string;
  autoRun: boolean;
}

class DeletedGobWeightReportCtrl implements DeletedGobWeightReportController {
  plants: Plant[];
  selectedPlant: Plant;
  startDate: Date;
  endDate: Date;
  form: angular.IFormController;
  private $stateParams: DeletedGobWeightReportParams;
  private $state: angular.ui.IStateService;

  results: apsViewModels.DeletedGobWeightReportItem[];
  searchPerformed: boolean;

  loaderService: LoaderService;
  reportService: ReportService;
  private $q: angular.IQService;

  constructor(
    $stateParams: DeletedGobWeightReportParams,
    $state: angular.ui.IStateService,
    $q: angular.IQService,
    navigationService: NavigationService,
    reportService: ReportService,
    loaderService: LoaderService
  ) {
    this.$q = $q;
    this.$state = $state;
    this.$stateParams = $stateParams;
    this.loaderService = loaderService;
    this.reportService = reportService;

    var today = moment().startOf('day');
    this.startDate = today.clone().subtract(1, 'month').toDate();
    this.endDate = today.toDate();

    var promise = navigationService.getNavigation().then((navModel) => {
      this.plants = navModel.plants();
      this.selectedPlant = navModel.selectedPlant;

      return this.loadState($stateParams);
    });

    loaderService.loadingPromise(promise);
  }

  loadState(params: DeletedGobWeightReportParams): angular.IPromise<any> {
    if (params.reportPlantId && params.reportPlantId !== params.plantId) {
      var plant = _.find(this.plants, {id: params.reportPlantId});
      if (plant) {
        this.selectedPlant = plant;
      }
    }

    if (params.startDate) {
      var startDate = moment(params.startDate);
      if (startDate.isValid()) {
        this.startDate = startDate.toDate();
      }
    }

    if (params.endDate) {
      var endDate = moment(params.endDate);
      if (endDate.isValid()) {
        this.endDate = endDate.toDate();
      }
    }

    if (params.autoRun) {
      return this.runGobWeightReport();
    }

    return this.$q.when();
  }

  exportToCsv = function() { 
    return _.orderBy(_.map<apsViewModels.DeletedGobWeightReportItem, any>(this.results, (x) => {
      let comments:string = '';

      _.each(x.comments, (c) => {
        comments = `${comments + (comments.length > 0 ? ' - ': '') + c.comment}`;
      });

      return {
        shop: x.shopName,
        jobRunLegacyId: x.jobRunLegacyId,   
        jobDescription: x.jobDescription,   
        gobName: x.gobName,   
        weight: x.weight,   
        weightTarget: x.weightTarget,   
        createdDetail: `${x.createdByFullName} - ${DateUtilityService.formatTime(x.createdOn, this.selectedPlant.timeZoneId)}`,   
        comments: comments,
        deletedDetail: `${x.deletedByFullName} - ${DateUtilityService.formatTime(x.deletedOn, this.selectedPlant.timeZoneId)}`,   
      };
    }), ['shopName', 'deletedOn'], ['asc', 'asc']);
  }

  saveState() {
    var reportPlantId = this.selectedPlant ? this.selectedPlant.id : undefined;
    if (reportPlantId === this.$stateParams.plantId) {
      reportPlantId = undefined;
    }
    var params: DeletedGobWeightReportParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: reportPlantId,
      startDate: this.startDate ? this.startDate.toISOString() : undefined,
      endDate: this.endDate ? this.endDate.toISOString() : undefined,
      autoRun: true
    };

    this.$state.transitionTo(States.DELETED_GOB_WEIGHT_REPORT, params);
  }

  generateReport() {
    if (this.form.$invalid) {
      return;
    }

    this.saveState();

    this.loaderService.loadingPromise(this.runGobWeightReport());
  }

  private runGobWeightReport(): angular.IPromise<any> {
    var startDateStart = moment(this.startDate).startOf('day').toDate();
    var tzOffsetStartDate = DateUtilityService.localToTimeZone(startDateStart, this.selectedPlant.timeZoneId).toDate();

    var endDateEnd = moment(this.endDate || new Date()).endOf('day').toDate();
    var tzOffsetEndDate = DateUtilityService.localToTimeZone(endDateEnd, this.selectedPlant.timeZoneId).toDate();

    return this.reportService.getDeletedGobWeightsReport(this.selectedPlant.id, tzOffsetStartDate, tzOffsetEndDate)
      .then(reportItems => {
        this.results = reportItems;
        this.searchPerformed = true;
      });
  }

  formatTime(time: Date): string {
    return DateUtilityService.formatTime(time, this.selectedPlant.timeZoneId);
  }
}

export default angular.module('aps.controller.deletedGobWeightReport', requires)
  .config(function ($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.DELETED_GOB_WEIGHT_REPORT,
      url: '/admin/reports/deletedGobWeights?startDate&endDate&autoRun&reportPlantId',
      params: {
        startDate: {dynamic: true},
        endDate: {dynamic: true},
        autoRun: {dynamic: true},
        reportPlantId: {dynamic: true},
      },
      controller: 'DeletedGobWeightReportCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Deleted Gob Weight Report'
      }
    });
  })
  .controller('DeletedGobWeightReportCtrl', DeletedGobWeightReportCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/reports/deletedGobWeightReport.controller.ts